import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { useDispatch } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';
import { format } from 'date-fns';

import { AppDispatch } from '@/store';
import { splitPunch } from '@/store/slices/timeClockSlice';
import TimeField from '@/components/TimeField';
import { colors, font, space, radius } from '@/theme/chalkCourt';
import { minutesBetweenTimes, time24ToDisplay } from '@/utils/timeParser';

/**
 * A clock-out that was really the next clock-in: worked until the shift ended,
 * forgot to clock out, then tapped right before the next shift — the tap landed
 * as the clock-out.
 */
export interface SplitTarget {
  punchId: number;
  /** The clock-out being turned into a clock-in. */
  punchTime: Date;
  /** The clock-in that opened the stretch (9:58). */
  inTime: Date;
  /** Where the earlier stretch was scheduled to end — the clock-out prefill. */
  scheduledEnd: Date;
  /** The shift the punch now starts. */
  nextLabel: string;
  /** That shift's scheduled end, when it's already over — the next clock-out prefill. */
  nextEnd: Date | null;
}

interface Props {
  target: SplitTarget | null;
  onClose: () => void;
  onFixed: (message: string) => void;
}

const hhmm = (d: Date) => format(d, 'HH:mm');
const show = (d: Date) => format(d, 'h:mm a');

const fmtLength = (minutes: number | null) => {
  if (minutes === null || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * "I clocked out at 12:00 — my 12:29 punch was my clock-in for the next class."
 * Adds the 12:00 clock-out, turns 12:29 into a clock-in and adds the clock-out
 * that ends the next class, in one request.
 */
const SplitPunchModal: React.FC<Props> = ({ target, onClose, onFixed }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [outTime, setOutTime] = useState('');
  const [nextOutTime, setNextOutTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setOutTime(hhmm(target.scheduledEnd));
      setNextOutTime(target.nextEnd ? hhmm(target.nextEnd) : '');
      setError(null);
    }
  }, [target?.punchId]);

  if (!target) return null;
  const punch = hhmm(target.punchTime);
  const first = outTime ? fmtLength(minutesBetweenTimes(hhmm(target.inTime), outTime)) : '';
  const second = nextOutTime ? fmtLength(minutesBetweenTimes(punch, nextOutTime)) : '';

  const submit = async () => {
    if (!outTime) {
      setError(`Enter when you clocked out before ${show(target.punchTime)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await dispatch(
      splitPunch({ punchId: target.punchId, clockOut: outTime, nextClockOut: nextOutTime || undefined })
    );
    setBusy(false);
    if (splitPunch.fulfilled.match(result) && result.payload.success) {
      onFixed(result.payload.message);
      onClose();
    } else {
      const msg = splitPunch.fulfilled.match(result) ? result.payload.message : (result.payload as string);
      setError(msg || "Couldn't fix your punches. Try again.");
    }
  };

  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={styles.backdrop}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <Text style={styles.title}>FIX MY PUNCHES</Text>
            <TouchableOpacity
              onPress={onClose}
              accessibilityLabel="close"
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>
          <Text style={styles.subtitle}>
            Your {show(target.punchTime)} punch becomes your clock-in for {target.nextLabel}. Add
            when you really clocked out before it
            {target.nextEnd ? `, and when you left ${target.nextLabel}` : ''}.
          </Text>

          <ScrollView keyboardShouldPersistTaps="handled" style={styles.form}>
            <TimeField label="I CLOCKED OUT AT" value={outTime} onChange={setOutTime} />
            {target.nextEnd && (
              <TimeField
                label={`I LEFT ${target.nextLabel.toUpperCase()} AT`}
                value={nextOutTime}
                onChange={setNextOutTime}
              />
            )}

            <View style={styles.preview}>
              <Text style={styles.previewHead}>YOUR TIME WILL READ</Text>
              <View style={styles.previewRow}>
                <Text style={styles.previewTime}>
                  {show(target.inTime)} → {outTime ? time24ToDisplay(outTime) : '—'}
                </Text>
                <Text style={styles.previewLen}>{first}</Text>
              </View>
              <View style={styles.previewRow}>
                <Text style={styles.previewTime}>
                  {show(target.punchTime)} →{' '}
                  {nextOutTime ? time24ToDisplay(nextOutTime) : target.nextEnd ? '—' : 'on the clock'}
                </Text>
                <Text style={styles.previewLen}>{second}</Text>
              </View>
            </View>

            {error && (
              <View style={styles.errorBox}>
                <Icon name="alert-circle" size={16} color={colors.accentDark} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}
          </ScrollView>

          <TouchableOpacity
            style={[styles.submit, busy && styles.submitBusy]}
            activeOpacity={0.85}
            disabled={busy}
            onPress={submit}>
            <Text style={styles.submitText}>{busy ? 'SAVING…' : 'SAVE CHANGES'}</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
    maxHeight: '92%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: font.display,
    fontSize: 30,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    marginTop: space.xs,
  },
  form: {
    marginTop: space.sm,
  },
  preview: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    padding: space.md,
    marginTop: space.md,
    gap: 6,
  },
  previewHead: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.6,
    color: colors.inkFaint,
  },
  previewRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  previewTime: {
    fontFamily: font.bodySemi,
    fontSize: 15,
    color: colors.ink,
  },
  previewLen: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: radius.sm,
    padding: space.sm,
    marginTop: space.md,
  },
  errorText: {
    flex: 1,
    marginLeft: space.xs,
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
  },
  submit: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: space.lg,
  },
  submitBusy: {
    opacity: 0.5,
  },
  submitText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default SplitPunchModal;
